import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  PesvPuntosCriticosDesplazamiento,
  PesvPuntosCriticosDesplazamientoPayload,
  PesvPuntosCriticosDesplazamientoService,
} from "../../../../../shared/services/PesvPuntosCriticosDesplazamiento.service";
import { PesvDesplazamientosService } from "../../../../../shared/services/pesvDesplazamientos.service";
import { NivelRiesgoService } from "../../../../../shared/services/nivel-riesgo.service";
import { TipoPuntoService } from "../../../../../shared/services/tipo-punto.service";


@Component({
  selector: "app-puntos-criticos",
  templateUrl: "./puntos-criticos.component.html",
  styleUrls: ["./puntos-criticos.component.scss"],
})
export class PuntosCriticosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  // Desplazamiento padre
  idDesplazamiento!: number;
  desplazamiento: any = null;

  get desplazamientoActivo(): boolean {
    return Number(this.desplazamiento?.estado) === 1;
  }

  puntoForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idPuntoEditando: number | null = null;

  // Catálogos (tipo_punto y nivel_riesgo son numéricos en el backend)
  tiposPunto: any[] = [];


  // Listado
  puntos: PesvPuntosCriticosDesplazamiento[] = [];
  puntosFiltrados: PesvPuntosCriticosDesplazamiento[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;
  nivelesRiesgo: any[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private puntosService: PesvPuntosCriticosDesplazamientoService,
    private desplazamientosService: PesvDesplazamientosService,
    private nivelRiesgoService: NivelRiesgoService,
    private tipoPuntoService: TipoPuntoService,
  ) {}

  ngOnInit(): void {
    this.idDesplazamiento = Number(this.route.snapshot.paramMap.get("id"));

    if (!this.idDesplazamiento) {
      Swal.fire("Error", "No se recibió un desplazamiento válido.", "error").then(() =>
        this.volver(),
      );
      return;
    }

    this.inicializarFormulario();
    this.cargarDesplazamiento();
    this.cargarPuntos();
    this.cargarNivelesRiesgo();
    this.cargarTiposPunto();
  }

  private cargarTiposPunto(): void {
    this.tipoPuntoService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.tiposPunto = Array.isArray(response) ? response : response?.data ?? [];
      });
  }


  private inicializarFormulario(): void {
    this.puntoForm = this.fb.group({
      ubicacion: ["", Validators.required],
      tipo_punto: [null],
      nivel_riesgo: [null],
      descripcion: [""],
      medida_control: [""],
    });
  }

  // ============ Desplazamiento padre ============
  private cargarDesplazamiento(): void {
    this.desplazamientosService
      .getByIdRiesgo(this.idDesplazamiento)
      .pipe(catchError(() => of(null)))
      .subscribe((respuesta: any) => {
        const data = respuesta?.data ?? respuesta;
        this.desplazamiento = Array.isArray(data) ? data[0] ?? null : data ?? null;
        this.activeTab = this.desplazamientoActivo ? 1 : 2;
      });
  }

    private cargarNivelesRiesgo(): void {
    this.nivelRiesgoService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.nivelesRiesgo = Array.isArray(response) ? response : response?.data ?? [];
      });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ Helpers de presentación ============
  nombreTipo(id?: number | null): string {
    if (id === null || id === undefined) return "-";
    return this.tiposPunto.find((t) => t.id === Number(id))?.nombre || "-";
  }


  claseNivel(id?: number | null): string {
    switch (Number(id)) {
      case 1:
        return "bg-success";
      case 2:
        return "bg-warning text-dark";
      case 3:
        return "bg-danger";
      case 4:
        return "bg-dark";
      default:
        return "bg-secondary";
    }
  }

  // ============ Listado ============
  cargarPuntos(): void {
    this.cargandoListado = true;
    this.puntosService.getByDesplazamiento(this.idDesplazamiento).subscribe({
      next: (data: any) => {
        this.puntos = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.puntos = [];
        this.puntosFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.puntosFiltrados = this.puntos.filter((p) => {
      const coincideTexto =
        !termino ||
        [
          p.ubicacion,
          p.descripcion,
          p.medida_control,
          this.nombreTipo(p.tipo_punto),
          p.nivel_riesgo,
        ]
          .filter((v) => v && v !== "-")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || p.estado === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aNumeroONull(valor: any): number | null {
    return valor === null || valor === undefined || valor === "" ? null : Number(valor);
  }

  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): PesvPuntosCriticosDesplazamientoPayload {
    const raw = this.puntoForm.getRawValue();
    return {
      desplazamiento_id: this.idDesplazamiento,
      ubicacion: String(raw.ubicacion).trim(),
      descripcion: this.aTextoONull(raw.descripcion),
      tipo_punto: this.aNumeroONull(raw.tipo_punto),
      nivel_riesgo: this.aNumeroONull(raw.nivel_riesgo),
      medida_control: this.aTextoONull(raw.medida_control),
    };
  }

  guardar(): void {
    if (!this.modoEdicion && !this.desplazamientoActivo) {
      Swal.fire("Desplazamiento inactivo", "No se pueden crear puntos críticos para un desplazamiento inactivo.", "warning");
      return;
    }

    if (this.puntoForm.invalid) {
      this.puntoForm.markAllAsTouched();
      Swal.fire("Campos obligatorios", "Completa todos los campos requeridos.", "warning");
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idPuntoEditando
        ? this.puntosService.update(this.idPuntoEditando, payload)
        : this.puntosService.create({ ...payload, estado: 1 });

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Punto crítico guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarPuntos();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el punto crítico.",
          "error",
        );
      },
    });
  }

  editar(punto: PesvPuntosCriticosDesplazamiento): void {
    if (Number(punto.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar puntos críticos activos.", "warning");
      return;
    }

    this.modoEdicion = true;
    this.idPuntoEditando = punto.id ?? null;
    this.puntoForm.patchValue({
      ubicacion: punto.ubicacion,
      tipo_punto: punto.tipo_punto ?? null,
      nivel_riesgo: punto.nivel_riesgo ?? null,
      descripcion: punto.descripcion ?? "",
      medida_control: punto.medida_control ?? "",
    });
    this.activeTab = 1;
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idPuntoEditando = null;
    this.puntoForm.reset();
    if (!this.desplazamientoActivo) this.activeTab = 2;
  }

  // ============ Activar / Inactivar ============
  inactivar(punto: PesvPuntosCriticosDesplazamiento): void {
    Swal.fire({
      title: "¿Inactivar punto crítico?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !punto.id) return;
      this.puntosService.inactivar(punto.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Punto crítico inactivado.", "success");
          this.cargarPuntos();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el punto crítico.", "error"),
      });
    });
  }

  activar(punto: PesvPuntosCriticosDesplazamiento): void {
    Swal.fire({
      title: "¿Activar punto crítico?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !punto.id) return;
      this.puntosService.activar(punto.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Punto crítico activado.", "success");
          this.cargarPuntos();
        },
        error: () => Swal.fire("Error", "No se pudo activar el punto crítico.", "error"),
      });
    });
  }

  // ============ Navegación ============
  volver(): void {
    this.location.back();
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }
}